import React, { useEffect, useState } from "react";
import "./App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { loadCatalog, API } from "./lib/store";
import { trackPageView, recordVisit } from "./lib/analytics";
import { applyTheme } from "./lib/theme";
import { initInteraction, initVisitTracking } from "./lib/regalVoice";
import { setExperience, expOn } from "./lib/experience";
import Header from "./components/Header";

applyTheme(window.location.pathname);
initInteraction();
initVisitTracking();
import Footer, { WhatsAppFloat } from "./components/Footer";
import UnderConstruction from "./components/UnderConstruction";
import { RegalAdvisor } from "./components/RegalAdvisor";
import HeyRegal from "./components/HeyRegal";
import { GoldCursor } from "./components/GoldCursor";
import { RegalVoiceLayer } from "./components/RegalVoiceLayer";
import { OccasionBar } from "./components/OccasionReminder";
import { NoticeProvider, DEFAULT_CC_NOTICE, ReviewLinkProvider } from "./lib/notice";
import Catalog from "./pages/Catalog";
import Collections from "./pages/Collections";
import CollectionPage from "./pages/CollectionPage";
import ProductDetail from "./pages/ProductDetail";
import BookMeeting from "./pages/BookMeeting";
import Contact from "./pages/Contact";
import Education from "./pages/Education";
import EducationGuide from "./pages/EducationGuide";
import NotFound from "./pages/NotFound";
import Admin from "./pages/Admin";
import Wishlist from "./pages/Wishlist";
import Finder from "./pages/Finder";
import Stories from "./pages/Stories";
import StoryPlayer from "./pages/StoryPlayer";
import AuthCallback from "./components/AuthCallback";
import { AuthProvider } from "./lib/auth";
import { EditProvider } from "./lib/content";
import { EditToggle } from "./components/edit/EditKit";
import DynamicPage from "./pages/DynamicPage";
import GoldRateChicago from "./pages/GoldRateChicago";

const RegalWorld = React.lazy(() => import("./pages/RegalWorld"));
const RegalWorldPage = React.lazy(() => import("./pages/RegalWorldPage"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView(pathname);
    recordVisit(pathname);
  }, [pathname]);
  return null;
}

// Scroll-reveal: gold accent line draws in under section titles as they enter view
function useScrollReveal(pathname) {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add("in-view");
          io.unobserve(e.target);
        }
      }),
      { threshold: 0.15 }
    );
    const t = setTimeout(() => {
      document.querySelectorAll(".section-title, [data-reveal]").forEach((el) => {
        el.classList.add("reveal-init");
        io.observe(el);
      });
    }, 150);
    return () => { clearTimeout(t); io.disconnect(); };
  }, [pathname]);
}

function AppRouter({ maintenance }) {
  const location = useLocation(); // read hash from here, not window.location.hash (not reactive)
  useScrollReveal(location.pathname);
  useEffect(() => {
    applyTheme(location.pathname);
  }, [location.pathname]);
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  if (maintenance && !location.pathname.startsWith("/admin")) {
    return <UnderConstruction />;
  }
  if (location.pathname.startsWith("/regal-world")) {
    return (
      <React.Suspense fallback={<div className="min-h-screen" style={{ background: "#ffffff" }} />}>
        <ScrollToTop />
        <Routes>
          <Route path="/regal-world" element={<RegalWorld />} />
          <Route path="/regal-world/:slug" element={<RegalWorldPage />} />
        </Routes>
        <GoldCursor active={expOn("goldCursor")} />
        {expOn("chatWidget") && <RegalAdvisor />}
        {expOn("voiceCommands") && <HeyRegal />}
      </React.Suspense>
    );
  }
  return (
    <>
      <ScrollToTop />
      <Header />
      <main key={location.pathname} className="page-fade">
        <Routes>
          <Route path="/" element={
            <React.Suspense fallback={<div className="min-h-[70vh]" />}>
              <RegalWorld embedded />
            </React.Suspense>
          } />
          <Route path="/shop" element={<Catalog />} />
          <Route path="/collections" element={<Collections />} />
          <Route path="/collections/:slug" element={<CollectionPage />} />
          <Route path="/product/:handle" element={<ProductDetail />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/finder" element={<Finder />} />
          <Route path="/stories" element={<Stories />} />
          <Route path="/stories/:slug" element={<StoryPlayer />} />
          <Route path="/book" element={<BookMeeting />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/gold-rate-chicago" element={<GoldRateChicago />} />
          <Route path="/education" element={<Education />} />
          <Route path="/education/:slug" element={<EducationGuide />} />
          <Route path="/p/:slug" element={<DynamicPage />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <WhatsAppFloat />
      <EditToggle />
      {expOn("chatWidget") && <RegalAdvisor />}
      {expOn("voiceCommands") && !location.pathname.startsWith("/admin") && <HeyRegal />}
      <GoldCursor active={expOn("goldCursor") && !location.pathname.startsWith("/admin")} />
      {expOn("voiceNarration") && <RegalVoiceLayer />}
      {expOn("occasionReminders") && <OccasionBar />}
    </>
  );
}

function App() {
  const [ready, setReady] = useState(false);
  const [maintenance, setMaintenance] = useState(false);
  const [ccNotice, setCcNotice] = useState(DEFAULT_CC_NOTICE);
  const [reviewLink, setReviewLink] = useState("");

  useEffect(() => {
    const fetchStatus = () =>
      axios.get(`${API}/site-status`)
        .then((r) => {
          setMaintenance(Boolean(r.data.maintenance));
          if (r.data.ccNotice) setCcNotice(r.data.ccNotice);
          setReviewLink(r.data.googleReviewLink || "");
          setExperience(r.data.experience);
        })
        .catch((e) => console.warn("Site status fetch failed:", e?.message));
    Promise.allSettled([loadCatalog(), fetchStatus()]).finally(() => setReady(true));
    window.addEventListener("rj-notice-updated", fetchStatus);
    return () => window.removeEventListener("rj-notice-updated", fetchStatus);
  }, []);

  if (!ready) {
    if (window.location.pathname.startsWith("/regal-world")) {
      return <div className="min-h-screen" style={{ background: "#ffffff" }} />;
    }
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="font-heading text-2xl tracking-[0.2em] text-[#171310]">REGAL JEWELS</p>
          <p className="text-xs tracking-[0.35em] uppercase text-[#a8842c] mt-2 animate-pulse">Loading collection...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App min-h-screen">
      <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <EditProvider>
          <NoticeProvider value={ccNotice}>
            <ReviewLinkProvider value={reviewLink}>
              <AppRouter maintenance={maintenance} />
            </ReviewLinkProvider>
          </NoticeProvider>
          </EditProvider>
          <Toaster position="top-center" richColors offset={64} />
        </AuthProvider>
      </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
